import { FinishReason } from "@perceptron/api/apiContracts/chatCompletions";
import type { OpenEnum } from "@perceptron/api/apiContracts/openEnum";

import { TemplateDefinition } from "@assets/templates";

/**
 * Analytics event names used throughout the application
 */
export const ANALYTICS_EVENTS = {
  PAGE_VIEW: "$pageview",

  // Media events
  MEDIA_UPLOADED: "media_uploaded",
  MEDIA_DELETED: "media_deleted",

  // Model run events
  MODEL_RUN_STARTED: "model_run_started",
  MODEL_RUN_COMPLETED: "model_run_completed",
  MODEL_RUN_FAILED: "model_run_failed",
  MODEL_RUN_CANCELLED: "model_run_cancelled",

  // Auxiliary requests are linked to their original output run, not counted as new runs.
  TRACKING_RECOVERY_STARTED: "tracking_recovery_started",
  TRACKING_RECOVERY_COMPLETED: "tracking_recovery_completed",
  TRACKING_RECOVERY_FAILED: "tracking_recovery_failed",
  TRACKING_RECOVERY_CANCELLED: "tracking_recovery_cancelled",

  // Detect events. Mirrors the `model_run_*` pattern: the demo emits the full
  // client-side lifecycle (app="demo") alongside the gateway's server-side
  // lifecycle (app="api") — slice by the `app` property in PostHog.
  DETECT_STARTED: "detect_started",
  DETECT_COMPLETED: "detect_completed",
  DETECT_FAILED: "detect_failed",
  DETECT_CANCELLED: "detect_cancelled",

  // Template events
  TEMPLATE_SELECTED: "template_selected",
  EXAMPLES_PANEL_OPENED: "examples_panel_opened",
  EXAMPLES_PANEL_CLOSED: "examples_panel_closed",

  // Export code events
  EXPORT_CODE_OPENED: "export_code_opened",
  EXPORT_CODE_LANGUAGE_CHANGED: "export_code_language_changed",
  EXPORT_CODE_COPIED: "export_code_copied",
} as const;

export type AnalyticsEvent = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

interface TrackingRecoveryEventProperties {
  parent_run_id: string;
  session_id: string;
  model: string;
  provider: string;
  source_id: string;
  track_id: string;
  target_timestamp: number;
  prompt_version: string;
  timestamp: number;
}

/**
 * Type-safe property definitions for each analytics event
 */
export interface AnalyticsEventProperties {
  [ANALYTICS_EVENTS.TRACKING_RECOVERY_STARTED]: TrackingRecoveryEventProperties;
  [ANALYTICS_EVENTS.TRACKING_RECOVERY_COMPLETED]: TrackingRecoveryEventProperties & {
    status: "found" | "no_match";
    request_id?: string;
    duration_ms: number;
    prompt_tokens?: number;
    completion_tokens?: number;
    total_tokens?: number;
  };
  [ANALYTICS_EVENTS.TRACKING_RECOVERY_FAILED]: TrackingRecoveryEventProperties & {
    duration_ms: number;
    error_type: string;
  };
  [ANALYTICS_EVENTS.TRACKING_RECOVERY_CANCELLED]: TrackingRecoveryEventProperties & {
    duration_ms: number;
    error_type: string;
  };
  /**
   * Page view events
   */
  [ANALYTICS_EVENTS.PAGE_VIEW]: {
    page: string;
    referrer?: string;
    source?: string;
  };

  /**
   * Media events
   */
  [ANALYTICS_EVENTS.MEDIA_UPLOADED]: {
    mediaType: "image" | "video";
    fileSize: number;
    fileName: string;
    documentId?: string;
  };
  [ANALYTICS_EVENTS.MEDIA_DELETED]: {
    mediaId: string;
    mediaType: "image" | "video";
    documentId?: string;
  };

  /**
   * Model run events
   */
  [ANALYTICS_EVENTS.MODEL_RUN_STARTED]: {
    // Model information
    model: string;
    runId?: string;
    sessionId?: string;
    template?: TemplateDefinition | undefined;

    // Input metrics
    promptCharacterCount: number;
    numberOfImages: number;
    userAnnotationsCount: number;

    // Model parameters
    outputFormat?: string;

    // Timestamp
    timestamp: number;
  };
  [ANALYTICS_EVENTS.MODEL_RUN_COMPLETED]: {
    // Model information
    model: string;
    runId?: string;
    sessionId?: string;
    template?: TemplateDefinition | undefined;

    // Token metrics
    totalInputTokens?: number;
    totalOutputTokens?: number;

    // Performance metrics
    timeToFirstToken?: number; // milliseconds
    timeToLastToken?: number; // milliseconds
    duration: number; // total duration in milliseconds

    // Output characteristics
    outputFormat?: string;
    outputCharacterCount?: number;
    finishReason?: FinishReason;

    // Timestamp
    timestamp: number;
  };
  [ANALYTICS_EVENTS.MODEL_RUN_FAILED]: {
    // Model information
    model: string;
    runId?: string;
    sessionId?: string;
    template?: TemplateDefinition | undefined;

    // Error information
    error: string;
    errorCode?: string;

    // Partial metrics (if available before failure)
    partialInputTokens?: number;
    partialOutputTokens?: number;
    timeBeforeFailure?: number; // milliseconds

    // Timestamp
    timestamp: number;
  };
  [ANALYTICS_EVENTS.MODEL_RUN_CANCELLED]: {
    // Model information
    model: string;
    runId?: string;
    sessionId?: string;
    template?: TemplateDefinition | undefined;

    // Partial metrics (if available before cancellation)
    partialOutputTokens?: number;
    timeBeforeCancellation?: number; // milliseconds
    didEmitFirstToken: boolean;

    // Timestamp
    timestamp: number;
  };

  /**
   * Detect events
   *
   * Property keys are snake_case to mirror the gateway's server-side detect
   * events exactly (`DetectCommonFields` + the input subset of
   * `DetectCompletedEvent` in `product/service/utils/src/analytics`), so
   * demo (`app="demo"`) and gateway (`app="api"`) rows break down side by
   * side in PostHog. The gateway additionally stamps `key_uuid`/`kid` on its
   * events; those are server-only. This deliberately differs from the demo's
   * camelCase `model_run_*` keys. There is no `model` property: the demo's detect proxy
   * never specifies a model (the gateway chooses its own), so the client has
   * no honest model id to stamp.
   */
  [ANALYTICS_EVENTS.DETECT_STARTED]: {
    /** Gateway route this run targets (via the demo proxy): "/v1/detect". */
    route: string;
    /** Per-run client UUID tying this run's client events together (never forwarded to the gateway). */
    run_id: string;
    session_id?: string;

    // Input/config — same names and types as the gateway's `detect_completed`.
    category_count: number;
    /** Always 0 — the demo route sends no exemplars; kept for property parity. */
    exemplar_count: number;
    output: "box" | "point";
    enable_thinking: boolean;

    /** Demo-only: the sanitized category strings (trimmed, de-duped, ≤256). */
    categories: string[];

    timestamp: number;
  };
  [ANALYTICS_EVENTS.DETECT_COMPLETED]: {
    route: string;
    run_id: string;
    session_id?: string;

    // Input echo (same names as `detect_started`).
    category_count: number;
    exemplar_count: number;
    output: "box" | "point";
    enable_thinking: boolean;

    // Result subset the client observes (the proxy response carries no
    // usage/diagnostics; those live on the gateway's detect_completed).
    detection_count: number;
    /**
     * Distinct `mention` labels across the returned detections. When the
     * request had no categories these are model-chosen, so this is the only
     * signal for "how many categories did the model decide on".
     */
    detected_category_count: number;
    finish_reason: OpenEnum<"complete" | "max_tokens">;
    /** Client-perceived end-to-end latency, including the demo proxy and network. */
    duration_ms: number;

    timestamp: number;
  };
  [ANALYTICS_EVENTS.DETECT_FAILED]: {
    route: string;
    run_id: string;
    session_id?: string;

    // Error information
    error: string;
    /** Demo-only: proxied upstream HTTP status (400 validation · 429 quota · 500 connection). */
    status_code?: number;
    error_code?: string;
    time_before_failure_ms?: number;

    // Input echo (same names as `detect_started`) so failures slice by input.
    category_count: number;
    output: "box" | "point";
    enable_thinking: boolean;

    timestamp: number;
  };
  [ANALYTICS_EVENTS.DETECT_CANCELLED]: {
    route: string;
    run_id: string;
    session_id?: string;

    time_before_cancellation_ms: number;

    // Input echo (same names as `detect_started`).
    category_count: number;
    output: "box" | "point";
    enable_thinking: boolean;

    timestamp: number;
  };

  /**
   * Template events
   */
  [ANALYTICS_EVENTS.TEMPLATE_SELECTED]: {
    template: TemplateDefinition;
  };
  [ANALYTICS_EVENTS.EXAMPLES_PANEL_OPENED]: {
    source: "see_all_button";
  };
  [ANALYTICS_EVENTS.EXAMPLES_PANEL_CLOSED]: {
    source: "close_button" | "template_selected" | "user_typed";
  };

  /**
   * Export code events
   */
  [ANALYTICS_EVENTS.EXPORT_CODE_OPENED]: {
    model: string;
  };
  [ANALYTICS_EVENTS.EXPORT_CODE_LANGUAGE_CHANGED]: {
    language: "python" | "curl";
    model: string;
  };
  [ANALYTICS_EVENTS.EXPORT_CODE_COPIED]: {
    language: "python" | "curl";
    model: string;
  };
}
